import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { notify } from "../components/Notification";
import { useGetProfileQuery } from "../services";
import { baseApi } from "../services/api";
import { clearCsrfToken } from "../services/csrf";
import { UserProfileType } from "../utils";

interface UserContextType {
  user: UserProfileType | null;
  isLoading: boolean;
  getUser: () => void;
  endSession: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const [signedOut, setSignedOut] = useState(false);

  // `currentData`, not `data`: RTK Query keeps serving the last successful
  // `data` once a query is skipped, so reading it here would leave the user
  // signed in after `endSession` — the authenticated tree would stay mounted
  // and refetch against a cleared session cookie.
  const { currentData, isLoading, refetch } = useGetProfileQuery(undefined, {
    skip: signedOut,
  });

  const user: UserProfileType | null = useMemo(() => {
    if (!currentData) {
      return null;
    }
    return {
      name: currentData.firstName,
      lastName: currentData.lastName ?? "",
      email: currentData.email,
      authProvider: currentData.authProvider ?? "local",
      dateOfBirth: currentData.dateOfBirth ?? null,
      ageVerificationExempt: currentData.ageVerificationExempt ?? false,
      isAdmin: currentData.isAdmin ?? false,
    };
  }, [currentData]);

  useEffect(() => {
    if (currentData) {
      notify.clear();
    }
  }, [currentData]);

  const getUser = useCallback(() => {
    if (signedOut) {
      setSignedOut(false);
      return;
    }
    refetch();
  }, [signedOut, refetch]);

  /**
   * The single teardown for a session — logout, deactivation and deletion all
   * land here. Skipping `getProfile` avoids a pointless 401 round-trip, and
   * `resetApiState` purges every domain's cache so the next user to sign in on
   * this tab never sees the previous user's accounts, transactions or net worth.
   */
  const endSession = useCallback(() => {
    setSignedOut(true);
    clearCsrfToken();
    dispatch(baseApi.util.resetApiState());
  }, [dispatch]);

  return (
    <UserContext.Provider
      value={useMemo(
        () => ({ user, isLoading, getUser, endSession }),
        [user, isLoading, getUser, endSession],
      )}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return ctx;
};
